import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { BannerBar } from '../components/BannerBar';
import { AiImageNameDialog } from '../components/AiImageNameDialog';
import { formatDate, formatCurrency, STATUS_DISPLAY_NAMES, STATUS_COLORS } from '../lib/utils';
import { JobIcon } from '../lib/jobIcon';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sun,
  FileText,
  Clock,
  Download,
  Mail,
  MapPin,
  Phone,
  User,
  LogOut,
  Loader2,
  CheckCircle,
  Gauge,
  AlertCircle,
  MessageSquare,
  Plus,
  ChevronRight,
  Calendar,
  Zap,
  Home,
  Image,
  PoundSterling,
  CreditCard,
  Receipt,
  ClipboardCheck,
  Upload,
  Send,
  Camera,
  ArrowDown,
  Milestone,
  Moon,
  Shield,
  Trash2,
  Bell,
  FileSignature,
  Eye,
  Folder,
  FolderOpen,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { useTheme } from '../contexts/ThemeContext';
import { SignVariationDialog } from '../components/SignVariationDialog';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';
// Dark/colored logo for light backgrounds
const LOGO_DARK_URL = 'https://customer-assets.emergentagent.com/job_f9f64a4c-3451-4815-8703-6feb7c894858/artifacts/mema1897_Updated%20Logo.png';

const TICKET_CATEGORIES = [
  { value: 'general', label: 'General Enquiry' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'installation', label: 'Installation Query' },
];

export const CustomerPortalDashboard = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [project, setProject] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docSearch, setDocSearch] = useState('');
  const [variations, setVariations] = useState([]);
  // Unified contracts/variations/LoAs list from /portal/contracts — surfaced
  // in the Contracts & Documents card so the customer can see at a glance
  // what's awaiting their signature.
  const [contracts, setContracts] = useState([]);
  const [signVariation, setSignVariation] = useState(null);
  const [emails, setEmails] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [checklistProgress, setChecklistProgress] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [activeJobId, setActiveJobId] = useState(null);
  const [switchingJob, setSwitchingJob] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const [approvingQuote, setApprovingQuote] = useState(null);
  const [declineDialog, setDeclineDialog] = useState({ open: false, quote: null });
  const [declineReason, setDeclineReason] = useState('');
  const [declineNotes, setDeclineNotes] = useState('');
  const [decliningQuote, setDecliningQuote] = useState(false);
  
  // New Ticket Dialog
  const [ticketDialog, setTicketDialog] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', category: 'general' });
  const [submittingTicket, setSubmittingTicket] = useState(false);
  
  // View Ticket Dialog
  const [viewTicket, setViewTicket] = useState(null);
  const [ticketReply, setTicketReply] = useState('');
  const [sendingReply, setSendingReply] = useState(false);

  // Messaging
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Timeline
  const [timeline, setTimeline] = useState(null);

  // DNO requirements (MPAN + cut-out photo) — drives the "on hold" banner
  const [dnoReq, setDnoReq] = useState({ contract_signed: false, needs_mpan: false, needs_cutout: false, mpan: '', has_cutout: false });
  const [mpanDialogOpen, setMpanDialogOpen] = useState(false);
  const [mpanInput, setMpanInput] = useState('');
  const [savingMpan, setSavingMpan] = useState(false);
  const [scanningMpan, setScanningMpan] = useState(false);
  const [uploadingCutout, setUploadingCutout] = useState(false);
  const [downloadingSigned, setDownloadingSigned] = useState(false);

  // Gallery
  const [gallery, setGallery] = useState([]);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  // Quote document
  const [quoteDoc, setQuoteDoc] = useState(null);

  // Document Upload
  const [uploading, setUploading] = useState(false);
  const [aiPrompt, setAiPrompt] = useState(null);
  const aiResolverRef = useRef(null);
  const SUPPORTED_IMG = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];

  const askPortalImageName = (file) => new Promise((resolve) => {
    aiResolverRef.current = resolve;
    const base = file.name.replace(/\.[^.]+$/, '');
    const previewUrl = URL.createObjectURL(file);
    setAiPrompt({ file, originalName: file.name, originalBase: base, previewUrl, suggestion: null, loading: true });
    const token = localStorage.getItem('portal_token');
    const fd = new FormData();
    fd.append('file', file);
    api.post('/portal/suggest-image-name', fd, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } })
      .then((res) => setAiPrompt((p) => p ? { ...p, suggestion: res.data?.suggested_name || null, loading: false } : p))
      .catch(() => setAiPrompt((p) => p ? { ...p, suggestion: null, loading: false } : p));
  });

  const resolvePortalAiPrompt = (chosenName) => {
    if (aiPrompt?.previewUrl) URL.revokeObjectURL(aiPrompt.previewUrl);
    const r = aiResolverRef.current;
    aiResolverRef.current = null;
    setAiPrompt(null);
    r?.(chosenName);
  };

  // Energy Calculator
  const [calcResult, setCalcResult] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcInputs, setCalcInputs] = useState({
    system_size_kw: 4.0,
    annual_consumption_kwh: 3500,
    tariff_rate: 0.28,
    export_rate: 0.15,
    battery_capacity_kwh: 0,
    self_consumption_pct: 0.40,
  });

  useEffect(() => {
    const storedUser = localStorage.getItem('portal_user');
    if (!storedUser) {
      navigate('/portal/login');
      return;
    }
    setUser(JSON.parse(storedUser));
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    const token = localStorage.getItem('portal_token');
    if (!token) {
      navigate('/portal/login');
      return;
    }

    // Configure API with portal token
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    try {
      const [projectRes, historyRes, docsRes, emailsRes, ticketsRes, checklistRes, invoicesRes, messagesRes, unreadRes, timelineRes, galleryRes, quoteRes, quotesListRes, variationsRes, contractsRes, jobsRes, dnoReqRes] = await Promise.all([
        api.get('/portal/my-project').catch(() => ({ data: null })),
        api.get('/portal/status-history').catch(() => ({ data: { history: [] } })),
        api.get('/portal/documents').catch(() => ({ data: [] })),
        api.get('/portal/emails').catch(() => ({ data: [] })),
        api.get('/portal/tickets').catch(() => ({ data: [] })),
        api.get('/portal/checklist-progress').catch(() => ({ data: null })),
        api.get('/portal/invoices').catch(() => ({ data: [] })),
        api.get('/portal/messages').catch(() => ({ data: [] })),
        api.get('/portal/messages/unread-count').catch(() => ({ data: { count: 0 } })),
        api.get('/portal/timeline').catch(() => ({ data: null })),
        api.get('/portal/gallery').catch(() => ({ data: [] })),
        api.get('/portal/quote-document').catch(() => ({ data: { has_quote: false } })),
        api.get('/portal/quotes').catch(() => ({ data: [] })),
        api.get('/portal/variations').catch(() => ({ data: [] })),
        api.get('/portal/contracts').catch(() => ({ data: [] })),
        api.get('/portal/jobs').catch(() => ({ data: { jobs: [] } })),
        api.get('/portal/dno-requirements').catch(() => ({ data: null })),
      ]);

      setProject(projectRes.data);
      setStatusHistory(historyRes.data?.history || []);
      setDocuments(docsRes.data || []);
      setEmails(emailsRes.data || []);
      setTickets(ticketsRes.data || []);
      setChecklistProgress(checklistRes.data);
      setInvoices(invoicesRes.data || []);
      setMessages(messagesRes.data || []);
      setUnreadCount(unreadRes.data?.count || 0);
      setTimeline(timelineRes.data);
      setGallery(galleryRes.data || []);
      setQuoteDoc(quoteRes.data?.has_quote ? quoteRes.data : null);
      setQuotes(quotesListRes.data || []);
      setVariations(variationsRes.data || []);
      setContracts(contractsRes.data || []);
      setJobs(jobsRes.data?.jobs || []);
      setActiveJobId(jobsRes.data?.active_job_id || null);
      if (dnoReqRes.data) { setDnoReq(dnoReqRes.data); setMpanInput(dnoReqRes.data.mpan || ''); }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        toast.error('Failed to load project data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('portal_token');
    localStorage.removeItem('portal_user');
    navigate('/portal/login');
  };

  const refreshDnoReq = async () => {
    try {
      const { data } = await api.get('/portal/dno-requirements');
      if (data) setDnoReq(data);
    } catch { /* non-blocking */ }
    try {
      const { data } = await api.get('/portal/documents');
      setDocuments(data || []);
    } catch { /* non-blocking */ }
  };

  // FastAPI 422s return `detail` as an array of objects — never hand that to
  // toast/JSX directly or React crashes. Always coerce to a string.
  const errText = (e, fallback) => {
    const d = e?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(', ') || fallback;
    return fallback;
  };
  const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

  const submitPortalMpan = async () => {
    const mpan = (mpanInput || '').trim();
    if (!mpan) { toast.error('Please enter your MPAN'); return; }
    setSavingMpan(true);
    try {
      await api.post('/portal/mpan', { mpan });
      toast.success('MPAN saved — thank you!');
      setMpanDialogOpen(false);
      await refreshDnoReq();
    } catch (e) {
      toast.error(errText(e, 'Could not save your MPAN'));
    } finally {
      setSavingMpan(false);
    }
  };

  const scanPortalBill = async (file) => {
    if (!file) return;
    setScanningMpan(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/portal/mpan-ocr', fd, MULTIPART);
      const bits = [];
      if (data?.mpan) { setMpanInput(data.mpan); bits.push('MPAN filled in'); }
      if (data?.annual_usage_kwh || data?.monthly_usage_kwh) bits.push('usage saved');
      if (data?.bill_saved) bits.push('bill added to your file');
      if (bits.length) {
        toast.success(`Bill scanned — ${bits.join(', ')}. Please check the MPAN, then save.`);
        if (data?.bill_saved) refreshDnoReq();
      } else {
        toast.error("Couldn't read the MPAN — please type it in");
      }
    } catch (e) {
      toast.error(errText(e, "Couldn't read the MPAN — please type it in"));
    } finally {
      setScanningMpan(false);
    }
  };

  const uploadPortalCutout = async (file) => {
    if (!file) return;
    setUploadingCutout(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post('/portal/cutout', fd, MULTIPART);
      toast.success('Photo uploaded — thank you!');
      await refreshDnoReq();
    } catch (e) {
      toast.error(errText(e, 'Could not upload your photo'));
    } finally {
      setUploadingCutout(false);
    }
  };

  const downloadSignedContract = async (packId) => {
    setDownloadingSigned(true);
    try {
      const res = await api.get(`/portal/contract-pack/${packId}/file`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Signed-Contract-${packId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      toast.error('Could not download your contract');
    } finally {
      setDownloadingSigned(false);
    }
  };

  const switchJob = async (jobId) => {
    if (jobId === activeJobId || switchingJob) return;
    setSwitchingJob(true);
    try {
      await api.post('/portal/switch-job', { job_id: jobId });
      setActiveJobId(jobId);
      setLoading(true);
      await fetchData();
      toast.success('Switched job');
    } catch (e) {
      toast.error('Could not switch job');
    } finally {
      setSwitchingJob(false);
    }
  };

  const handleDeclineQuoteSubmit = async () => {
    if (!declineDialog.quote) return;
    setDecliningQuote(true);
    try {
      await api.post(`/portal/quotes/${declineDialog.quote.id}/decline`, {
        reason: declineReason,
        notes: declineNotes,
        move_to_archived: true,
      });
      toast.success('Quote declined. Thanks for letting us know.');
      setDeclineDialog({ open: false, quote: null });
      setDeclineReason('');
      setDeclineNotes('');
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to decline quote');
    } finally {
      setDecliningQuote(false);
    }
  };

  const handleApproveQuote = async (quote) => {
    if (!quote) return;
    setApprovingQuote(quote.id);
    try {
      const r = await api.post(`/portal/quotes/${quote.id}/approve`);
      if (r.data.already_approved) {
        toast.info('Quote already approved');
      } else {
        toast.success('Quote approved — your deposit invoice will arrive shortly.');
      }
      await fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to approve quote');
    } finally {
      setApprovingQuote(null);
    }
  };

  const handleDownloadInvoicePdf = async (invoiceId, label = 'Invoice') => {
    try {
      const token = localStorage.getItem('portal_token');
      // Use a relative URL so the browser hits the same origin the portal is
      // served from. Prevents prod-vs-preview cross-origin/auth mismatches when
      // REACT_APP_BACKEND_URL was baked to the wrong host at build time.
      const resp = await fetch(`/api/portal/invoices/${invoiceId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || `Could not download ${label.toLowerCase()}`);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Failed to download ${label.toLowerCase()}`);
    }
  };

  const handlePayInvoice = async (invoice, paymentKind) => {
    setPayingInvoice(invoice.id + (paymentKind ? `-${paymentKind}` : ''));
    try {
      const body = paymentKind ? { payment_kind: paymentKind } : {};
      const response = await api.post(`/portal/invoices/${invoice.id}/pay`, body);
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        toast.error('Failed to create payment session');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to initiate payment';
      toast.error(msg);
    } finally {
      setPayingInvoice(null);
    }
  };

  const [syncingFolderZip, setSyncingFolderZip] = useState(null);
  const handleDownloadFolder = async (folderName) => {
    setSyncingFolderZip(folderName);
    try {
      const token = localStorage.getItem('portal_token');
      const response = await api.get(`/portal/download-folder`, {
        params: { folder: folderName },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${folderName.replace(/[^a-zA-Z0-9 _-]+/g, '-')}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded all ${folderName} documents`);
    } catch (error) {
      toast.error('Could not download this folder right now');
    } finally {
      setSyncingFolderZip(null);
    }
  };

  const handleDownloadReport = async (type) => {
    try {
      const token = localStorage.getItem('portal_token');
      const response = await api.get(`/portal/report/${type}`, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type === 'summary' ? 'Project_Summary' : 'Full_Report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  // Open a contract / variation / LoA PDF in a new tab. Uses the portal
  // bearer token (the file endpoints are auth-gated) and a relative URL so it
  // hits the same origin the portal is served from.
  const openContractFile = async (c) => {
    if (!c?.view_url) return;
    try {
      const token = localStorage.getItem('portal_token');
      const resp = await fetch(c.view_url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Could not open document');
        return;
      }
      const blob = await resp.blob();
      const head = await blob.slice(0, 5).text();
      if (!head.startsWith('%PDF')) {
        toast.error('This document appears to be corrupted. Please contact Helios.');
        return;
      }
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not open document');
    }
  };


  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.description) {
      toast.error('Please fill in all fields');
      return;
    }

    setSubmittingTicket(true);
    try {
      const token = localStorage.getItem('portal_token');
      await api.post('/portal/tickets', newTicket, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Support ticket created');
      setTicketDialog(false);
      setNewTicket({ subject: '', description: '', category: 'general' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create ticket');
    } finally {
      setSubmittingTicket(false);
    }
  };

  const handleSendReply = async () => {
    if (!ticketReply.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingReply(true);
    try {
      const token = localStorage.getItem('portal_token');
      await api.post(`/portal/tickets/${viewTicket.id}/reply`, 
        { message: ticketReply },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Reply sent');
      setTicketReply('');
      
      // Refresh ticket data
      const ticketsRes = await api.get('/portal/tickets', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(ticketsRes.data || []);
      
      // Update the viewed ticket
      const updatedTicket = ticketsRes.data.find(t => t.id === viewTicket.id);
      if (updatedTicket) {
        setViewTicket(updatedTicket);
      }
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB.');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('portal_token');
      let displayName = null;
      if (SUPPORTED_IMG.includes(file.type)) {
        displayName = await askPortalImageName(file);
      }
      const formData = new FormData();
      formData.append('file', file);
      if (displayName) formData.append('display_name', displayName);
      await api.post('/portal/upload-document', formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Messaging handler
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      const token = localStorage.getItem('portal_token');
      await api.post('/portal/messages', { message: newMessage }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage('');
      // Refresh messages
      const res = await api.get('/portal/messages');
      setMessages(res.data || []);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const messagesEndRef = React.useRef(null);

  // Energy Calculator
  const handleAutoCalculate = async () => {
    setCalcLoading(true);
    try {
      const res = await api.post('/portal/energy-calculator/auto');
      setCalcResult(res.data);
      if (res.data.system_size_kw) {
        setCalcInputs(prev => ({
          ...prev,
          system_size_kw: res.data.system_size_kw,
          battery_capacity_kwh: res.data.battery_capacity_kwh || 0,
        }));
      }
    } catch {
      // Fallback to manual
      handleCalculate();
    } finally {
      setCalcLoading(false);
    }
  };

  const handleCalculate = async () => {
    setCalcLoading(true);
    try {
      const res = await api.post('/portal/energy-calculator', calcInputs);
      setCalcResult(res.data);
    } catch {
      toast.error('Failed to calculate');
    } finally {
      setCalcLoading(false);
    }
  };
  React.useEffect(() => {
    if (activeTab === 'messages' && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeTab]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center dark:bg-slate-950">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950" data-testid="customer-portal-dashboard">
      {/* Header */}
      <header className="bg-slate-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Helios" className="h-10" onError={(e) => { e.target.style.display = 'none'; }} />
              <div>
                <h1 className="text-lg font-bold">Customer Portal</h1>
                <p className="text-slate-400 text-sm">Welcome, {user?.first_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="text-slate-300 hover:text-white hover:bg-slate-800 h-9 w-9"
                data-testid="portal-theme-toggle-btn"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-amber-400" />
                ) : (
                  <Moon className="w-5 h-5" />
                )}
              </Button>
              <Button variant="ghost" onClick={handleLogout} className="text-slate-300 hover:text-white hover:bg-slate-800">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </header>

      <BannerBar endpoint="/banners/portal" />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {!project ? (
          <Card className="text-center py-12">
            <CardContent>
              <AlertCircle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
              <h2 className="text-xl font-bold text-slate-900 mb-2">No Project Found</h2>
              <p className="text-slate-600 mb-6">
                We couldn't find a project linked to your email address.<br />
                Please contact Helios Electrical & Solar for assistance.
              </p>
              <Button onClick={() => setTicketDialog(true)}>
                <MessageSquare className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Project switcher — clear, labelled tabs when the customer has
                more than one project so they know they can switch. */}
            {jobs.length > 1 && (
              <div className="mb-6" data-testid="portal-jobs-switcher">
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="w-4 h-4 text-blue-600" />
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                    Your projects
                  </p>
                  <span className="text-xs text-slate-400">— tap a project to view it</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {jobs.map((j) => {
                    const active = j.id === activeJobId;
                    return (
                      <button
                        key={j.id}
                        type="button"
                        onClick={() => switchJob(j.id)}
                        disabled={switchingJob}
                        data-testid={`portal-job-tab-${j.id}`}
                        className={`text-left rounded-xl border px-4 py-2.5 transition-all disabled:opacity-60 ${
                          active
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/40 ring-1 ring-blue-500'
                            : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-blue-300'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {active && <CheckCircle className="w-4 h-4 text-blue-600" />}
                          <span className="text-blue-600 flex-shrink-0"><JobIcon job={j} className="w-4 h-4" /></span>
                          <span className={`font-medium text-sm ${active ? 'text-blue-700 dark:text-blue-300' : 'text-slate-800 dark:text-slate-100'}`}>
                            {j.job_label}
                          </span>
                        </span>
                        <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {j.status_display}{active ? ' · viewing now' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Status Banner */}
            <Card className="mb-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
              <CardContent className="py-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div>
                    <p className="text-blue-200 text-sm">Current Status</p>
                    <h2 className="text-2xl font-bold">{project.status_display || STATUS_DISPLAY_NAMES[project.status]}</h2>
                    {project.confirmed_install_date && (
                      <p className="text-blue-200 mt-1">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Installation: {formatDate(project.confirmed_install_date)}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {quoteDoc && (
                      <Button variant="secondary" asChild>
                        <a href={quoteDoc.url} download={quoteDoc.filename}>
                          <Download className="w-4 h-4 mr-2" />
                          Download Quote
                        </a>
                      </Button>
                    )}
                    <Button variant="secondary" onClick={() => handleDownloadReport('summary')}>
                      <Download className="w-4 h-4 mr-2" />
                      Summary PDF
                    </Button>
                    <Button variant="secondary" onClick={() => handleDownloadReport('full')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Full Report
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Deposit outstanding — pre-orders / DNO on hold */}
            {(() => {
              const dep = invoices.find(
                (i) => i.invoice_type === 'deposit'
                  && !['PAID', 'VOIDED', 'DELETED'].includes(i.status)
                  && !i.paid_via_stripe
                  && !i.deposit_paid
                  && !project.deposit_paid_date
              );
              if (!dep) return null;
              return (
                <Card className="mb-6 border-2 border-amber-300 bg-amber-50" data-testid="portal-deposit-hold-banner">
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3 flex-wrap">
                      <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-[220px]">
                        <p className="font-semibold text-amber-900">
                          Your deposit is outstanding — {formatCurrency(dep.amount || 0)}
                        </p>
                        <p className="text-sm text-amber-800 mt-1">
                          Please note we <strong>cannot place any pre-orders or progress your DNO
                          (grid connection) application until your deposit is paid</strong>. Paying
                          now keeps your installation moving.
                        </p>
                      </div>
                      <Button
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        onClick={() => setActiveTab('billing')}
                        data-testid="deposit-hold-pay-btn"
                      >
                        Pay deposit
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6 flex-wrap">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="billing">Billing</TabsTrigger>
                <TabsTrigger value="documents">Documents ({documents.length})</TabsTrigger>
                <TabsTrigger value="messages" className="relative">
                  Messages
                  {unreadCount > 0 && (
                    <span className="ml-1.5 inline-flex items-center justify-center w-5 h-5 text-[10px] font-bold bg-red-500 text-white rounded-full">
                      {unreadCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="gallery">Gallery</TabsTrigger>
                <TabsTrigger value="calculator">Savings Calculator</TabsTrigger>
                <TabsTrigger value="progress">History</TabsTrigger>
                <TabsTrigger value="emails">Emails ({emails.length})</TabsTrigger>
                <TabsTrigger value="support">Support ({tickets.length})</TabsTrigger>
                <TabsTrigger value="your-data">Your Data</TabsTrigger>
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview">
                {/* ACTION NEEDED — MPAN + meter-cupboard photo. Shown at the very
                    top whenever either is still outstanding, so the customer can
                    provide them any time from the dashboard. Clears automatically
                    once on file (via customer here, or a CRM user). */}
                {(dnoReq.needs_mpan || dnoReq.needs_cutout) && (
                  <Card className="mb-6 border-2 border-red-300 bg-red-50" data-testid="portal-dno-hold-banner">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-red-900 text-base">
                            Action needed — we can't book your installation yet
                          </div>
                          <div className="text-sm text-red-800 mt-1">
                            Please provide {dnoReq.needs_mpan && dnoReq.needs_cutout ? 'the two items' : 'the item'} below.
                            Without {dnoReq.needs_mpan && dnoReq.needs_cutout ? 'these' : 'this'} we <strong>cannot raise your DNO (grid connection) application</strong>,
                            and we can't start your installation until the network operator approves it — so this can delay your install.
                            It only takes a minute.
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            {dnoReq.needs_mpan && (
                              <Button
                                onClick={() => { setMpanInput(dnoReq.mpan || ''); setMpanDialogOpen(true); }}
                                className="bg-red-600 hover:bg-red-700 text-white"
                                data-testid="dno-hold-add-mpan-btn"
                              >
                                <Gauge className="w-4 h-4 mr-2" /> Add your MPAN
                              </Button>
                            )}
                            {dnoReq.needs_cutout && (
                              <label className="inline-flex">
                                <input
                                  type="file"
                                  accept="image/*"
                                  capture="environment"
                                  className="hidden"
                                  onChange={(e) => uploadPortalCutout(e.target.files?.[0])}
                                  data-testid="dno-hold-cutout-input"
                                />
                                <span className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer ${uploadingCutout ? 'bg-red-300 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}>
                                  {uploadingCutout ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</> : <><Camera className="w-4 h-4" /> Upload meter cupboard photo</>}
                                </span>
                              </label>
                            )}
                          </div>
                          {dnoReq.needs_mpan && (
                            <p className="text-xs text-red-700 mt-2">
                              Tip: you can just snap a photo of your electricity bill and we'll read the MPAN for you.
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
                {/* Your proposed system design — extracted from the proposal
                    and shared here so the customer can see their layout. */}
                {(() => {
                  const design = (documents || []).find(d => d.doc_tag === 'system_design_diagram');
                  if (!design) return null;
                  return (
                    <Card className="mb-6 border border-slate-200 overflow-hidden" data-testid="portal-system-design-card">
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <Sun className="w-5 h-5 text-amber-500" />
                          Your Proposed System Design
                        </CardTitle>
                        <p className="text-sm text-slate-500">
                          This is the solar system layout your installer has proposed for your property.
                        </p>
                      </CardHeader>
                      <CardContent>
                        <a
                          href={design.preview_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg overflow-hidden border border-slate-200 bg-slate-50 hover:border-amber-300 transition-colors"
                          data-testid="portal-system-design-image-link"
                        >
                          <img
                            src={design.preview_url}
                            alt="Proposed system design"
                            className="w-full max-h-[520px] object-contain bg-white"
                            loading="lazy"
                            data-testid="portal-system-design-image"
                          />
                        </a>
                        <div className="mt-2 text-xs text-slate-400 text-center">Tap the image to open it full size</div>
                      </CardContent>
                    </Card>
                  );
                })()}
                {/* MPAN entry dialog */}
                <Dialog open={mpanDialogOpen} onOpenChange={setMpanDialogOpen}>
                  <DialogContent data-testid="portal-mpan-dialog">
                    <DialogHeader>
                      <DialogTitle>Add your MPAN</DialogTitle>
                      <DialogDescription>
                        Your MPAN (electricity supply number) is at the top of any electricity bill — the "Supply Number" / S box, like the picture below.
                      </DialogDescription>
                    </DialogHeader>
                    <img
                      src="https://customer-assets-jt897jd0.emergentagent.net/job_317c24ef-1b68-48d8-b64e-56fdbe5ba447/artifacts/a8svq7ox_MPAN-number-example.jpg"
                      alt="Where to find your MPAN on an electricity bill"
                      className="w-full rounded border border-slate-200"
                      data-testid="portal-mpan-guide-img"
                    />
                    <label className="block">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={(e) => scanPortalBill(e.target.files?.[0])}
                        data-testid="portal-mpan-scan-input"
                      />
                      <span className={`w-full inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium cursor-pointer border ${scanningMpan ? 'bg-slate-100 text-slate-500 border-slate-200' : 'bg-white text-blue-700 border-blue-300 hover:bg-blue-50'}`}>
                        {scanningMpan ? <><Loader2 className="w-4 h-4 animate-spin" /> Reading your bill…</> : <><Camera className="w-4 h-4" /> Scan my bill to fill it in</>}
                      </span>
                    </label>
                    <Input
                      value={mpanInput}
                      onChange={(e) => setMpanInput(e.target.value)}
                      placeholder="e.g. 20 0001 2345 678"
                      className="font-mono"
                      data-testid="portal-mpan-input"
                    />
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setMpanDialogOpen(false)} data-testid="portal-mpan-cancel">Cancel</Button>
                      <Button onClick={submitPortalMpan} disabled={savingMpan || !mpanInput.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="portal-mpan-save">
                        {savingMpan ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />} Save MPAN
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                {/* Contract awaiting signature — prominent banner linking to
                    the live signing page. Surfaces contract packs, variations
                    and LoAs the customer still needs to sign. */}
                {(() => {
                  const pendingContracts = (contracts || []).filter(c => c.needs_customer_signature && c.sign_token);
                  if (pendingContracts.length === 0) return null;
                  const KIND_LABEL = { contract_pack: 'Contract', variation: 'Variation', loa: 'Letter of Authorisation' };
                  return (
                    <Card className="mb-6 border-2 border-amber-400 bg-amber-50" data-testid="portal-sign-contract-banner">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3 flex-wrap">
                          <FileSignature className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-[220px]">
                            <div className="font-semibold text-amber-900">
                              {pendingContracts.length === 1
                                ? 'Your contract is ready to sign'
                                : `${pendingContracts.length} documents are ready for your signature`}
                            </div>
                            <div className="text-xs text-amber-700 mt-0.5">
                              Please review and sign to move your project forward. This opens your live, secure signing page.
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 w-full sm:w-auto">
                            {pendingContracts.map((c) => {
                              const path = c.kind === 'contract_pack'
                                ? `/sign-contract/${c.sign_token}`
                                : `/sign-variation/${c.sign_token}`;
                              return (
                                <Button
                                  key={`${c.kind}-${c.id}`}
                                  asChild
                                  className="bg-amber-600 hover:bg-amber-700 text-white"
                                  data-testid={`portal-overview-sign-${c.id}`}
                                >
                                  <a href={path} target="_blank" rel="noreferrer">
                                    <FileSignature className="w-4 h-4 mr-2" />
                                    Sign {KIND_LABEL[c.kind] || 'document'} now
                                  </a>
                                </Button>
                              );
                            })}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
                {/* Signed confirmation — green "thank you" state shown right
                    after the customer signs (recently signed items only, so it
                    doesn't linger forever). Covers contract packs (awaiting our
                    counter-sign or fully signed) + variations / LoAs. */}
                {(() => {
                  const recentlySigned = (contracts || []).filter((c) => {
                    const ts = c.customer_signed_at || c.signed_at;
                    if (!ts) return false;
                    const isSigned = c.kind === 'contract_pack'
                      ? ['customer_signed', 'fully_signed'].includes(c.status)
                      : c.status === 'signed';
                    if (!isSigned) return false;
                    const days = (Date.now() - new Date(ts).getTime()) / 86400000;
                    return days >= 0 && days <= 7;
                  });
                  if (recentlySigned.length === 0) return null;
                  const KIND_LABEL = { contract_pack: 'Contract', variation: 'Variation', loa: 'Letter of Authorisation' };
                  return (
                    <Card className="mb-6 border-2 border-emerald-300 bg-emerald-50" data-testid="portal-signed-confirmation">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="w-6 h-6 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-emerald-900" data-testid="portal-signed-title">
                              Signed ✓ — thank you!
                            </div>
                            <div className="text-sm text-emerald-800 mt-0.5">
                              We&apos;ve received your signature. There&apos;s nothing more you need to do right now.
                            </div>
                            <div className="mt-3 space-y-1.5">
                              {recentlySigned.map((c) => {
                                const awaitingCounter = c.kind === 'contract_pack' && c.status === 'customer_signed';
                                return (
                                  <div key={`signed-${c.kind}-${c.id}`} className="flex flex-wrap items-center gap-2 text-sm text-emerald-900" data-testid={`portal-signed-item-${c.id}`}>
                                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                                    <span className="font-medium">{c.label || KIND_LABEL[c.kind]}</span>
                                    <span className="text-emerald-700 text-xs">
                                      {awaitingCounter
                                        ? '— signed by you, Helios is counter-signing. Your fully-signed copy will follow shortly.'
                                        : c.status === 'fully_signed'
                                        ? '— fully signed and complete.'
                                        : '— signed and received.'}
                                    </span>
                                    {c.kind === 'contract_pack' && c.status === 'fully_signed' && (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        disabled={downloadingSigned}
                                        onClick={() => downloadSignedContract(c.id)}
                                        className="h-7 px-2 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                                        data-testid={`portal-download-signed-${c.id}`}
                                      >
                                        {downloadingSigned ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                                        Download counter-signed copy
                                      </Button>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}
                {/* Quote awaiting approval banner */}
                {(() => {
                  const pendingQuote = quotes.find(q => ['SENT', 'DRAFT'].includes(q.status));
                  const unpaidInvoice = invoices.find(i => !['PAID', 'VOIDED', 'DELETED'].includes(i.status) && !i.paid_via_stripe);
                  if (!pendingQuote && !unpaidInvoice) return null;
                  return (
                    <Card className="mb-6 border-2 border-amber-300 bg-amber-50" data-testid="portal-billing-banner">
                      <CardContent className="p-5">
                        <div className="flex items-start gap-3 flex-wrap">
                          <Bell className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-[200px]">
                            {pendingQuote && (
                              <div className="mb-3">
                                <div className="font-semibold text-amber-900">
                                  Quote {pendingQuote.xero_invoice_number} awaiting your approval — {formatCurrency(pendingQuote.amount || 0)}
                                </div>
                                <div className="text-xs text-amber-700">
                                  Approving moves your project forward to deposit invoicing.
                                </div>
                              </div>
                            )}
                            {unpaidInvoice && !pendingQuote && (
                              <div>
                                <div className="font-semibold text-amber-900">
                                  Invoice {unpaidInvoice.xero_invoice_number} ready ({formatCurrency(unpaidInvoice.amount || 0)})
                                </div>
                                <div className="text-xs text-amber-700">Pay it from the Billing tab.</div>
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {pendingQuote && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadInvoicePdf(pendingQuote.id, `Quote-${pendingQuote.xero_invoice_number}`)}
                                  data-testid="banner-view-quote"
                                >
                                  <Download className="w-4 h-4 mr-1" /> View Quote
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                                  onClick={() => {
                                    setDeclineDialog({ open: true, quote: pendingQuote });
                                    setDeclineReason('');
                                    setDeclineNotes('');
                                  }}
                                  data-testid="banner-decline-quote"
                                >
                                  Decline
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                  onClick={() => handleApproveQuote(pendingQuote)}
                                  disabled={approvingQuote === pendingQuote.id}
                                  data-testid="banner-approve-quote"
                                >
                                  {approvingQuote === pendingQuote.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                                  Approve Quote
                                </Button>
                              </>
                            )}
                            {!pendingQuote && unpaidInvoice && (
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => setActiveTab('billing')}
                                data-testid="banner-go-billing"
                              >
                                Go to Billing
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Install Progress Stepper */}
                {project.status && (
                  <Card className="mb-6 border-2 border-amber-100 overflow-hidden" data-testid="install-tracker">
                    <CardContent className="p-6">
                      <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Your Installation Journey</h3>
                      <div className="flex items-center justify-between relative">
                        {/* Connecting line */}
                        <div className="absolute top-5 left-0 right-0 h-0.5 bg-slate-200" />
                        {[
                          { key: 'lead', label: 'Account Creation', idx: 0 },
                          { key: 'quote_sent', label: 'Quote Provided', idx: 1 },
                          { key: 'approved', label: 'Quote Accepted', idx: 2 },
                          { key: 'deposit_taken', label: 'Deposit Paid', idx: 3 },
                          { key: 'scheduled', label: 'Scheduled', idx: 4 },
                          { key: 'in_progress_installation', label: 'In Progress', idx: 5 },
                          { key: 'in_progress_invoiced', label: 'Install Complete', idx: 6 },
                          { key: 'in_progress_handover', label: 'Handover', idx: 7 },
                          { key: 'completed', label: 'Completed', idx: 8 },
                        ].map((step) => {
                          const stages = ['lead','qualified','quote_sent','approved','deposit_taken','scheduled','ordered','in_progress_installation','in_progress_invoiced','in_progress_handover','completed','archived'];
                          const currentIdx = stages.indexOf(project.status);
                          const stepIdx = stages.indexOf(step.key);
                          const isCompleted = currentIdx >= stepIdx && currentIdx >= 0;
                          const isCurrent = step.key === project.status || 
                            (step.key === 'in_progress_installation' && ['ordered','in_progress_installation'].includes(project.status)) ||
                            (step.key === 'quote_sent' && project.status === 'qualified');
                          return (
                            <div key={step.key} className="flex flex-col items-center relative z-10" style={{minWidth: '70px'}}>
                              <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                                isCurrent
                                  ? 'bg-amber-500 border-amber-500 text-white ring-4 ring-amber-100'
                                  : isCompleted
                                    ? 'bg-green-500 border-green-500 text-white'
                                    : 'bg-white border-slate-300 text-slate-400'
                              }`}>
                                {isCompleted && !isCurrent ? (
                                  <CheckCircle className="w-5 h-5" />
                                ) : (
                                  <span className="text-xs font-bold">{step.idx + 1}</span>
                                )}
                              </div>
                              <span className={`text-xs mt-2 text-center font-medium ${
                                isCurrent ? 'text-amber-700' : isCompleted ? 'text-green-700' : 'text-slate-400'
                              }`}>
                                {step.label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Contact Info */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Your Details
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="flex items-start gap-3">
                        <User className="w-4 h-4 text-slate-400 mt-1" />
                        <div>
                          <p className="font-medium">{project.first_name} {project.last_name}</p>
                          <p className="text-sm text-slate-500">Customer</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <MapPin className="w-4 h-4 text-slate-400 mt-1" />
                        <div>
                          <p className="font-medium">{project.address}</p>
                          <p className="text-sm text-slate-500">{project.city} {project.postcode}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <Mail className="w-4 h-4 text-slate-400 mt-1" />
                        <p>{project.email}</p>
                      </div>
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-slate-400 mt-1" />
                        <p>{project.phone}</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* System Details */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Sun className="w-5 h-5" />
                        System Specification
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-slate-500">System Size</p>
                          <p className="font-semibold text-lg">{project.system_size} kW</p>
                        </div>
                        <div>
                          {Number(project.vat_rate) > 0 ? (
                            <>
                              <p className="text-sm text-slate-500">Total Cost</p>
                              <div className="space-y-0.5" data-testid="portal-price-breakdown">
                                <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal (ex VAT)</span><span className="font-medium">{formatCurrency(Number(project.system_total_cost) || 0)}</span></div>
                                <div className="flex justify-between text-sm"><span className="text-slate-500">VAT ({Number(project.vat_rate)}%)</span><span className="font-medium">{formatCurrency((Number(project.system_total_cost) || 0) * (Number(project.vat_rate) / 100))}</span></div>
                                <div className="flex justify-between text-base pt-0.5 border-t"><span className="font-semibold">Total (inc VAT)</span><span className="font-bold">{formatCurrency((Number(project.system_total_cost) || 0) * (1 + Number(project.vat_rate) / 100))}</span></div>
                              </div>
                            </>
                          ) : (
                            <>
                              <p className="text-sm text-slate-500">Total Cost</p>
                              <p className="font-semibold text-lg" data-testid="portal-price-breakdown">{formatCurrency(Number(project.system_total_cost) || 0)}</p>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="pt-3 border-t space-y-2">
                        {project.panel_manufacturer && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Solar Panels</span>
                            <span className="font-medium text-right">
                              {project.panel_manufacturer}
                              {project.panel_model && <span className="text-slate-400 ml-1">— {project.panel_model}</span>}
                              {project.panel_quantity > 0 && <span className="text-slate-400 ml-1">x{project.panel_quantity}</span>}
                            </span>
                          </div>
                        )}
                        {project.inverter_manufacturer && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Inverter</span>
                            <span className="font-medium text-right">
                              {project.inverter_manufacturer}
                              {project.inverter_model && <span className="text-slate-400 ml-1">— {project.inverter_model}</span>}
                              {project.inverter_quantity > 0 && <span className="text-slate-400 ml-1">x{project.inverter_quantity}</span>}
                            </span>
                          </div>
                        )}
                        {project.battery_manufacturer && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Battery</span>
                            <span className="font-medium text-right">
                              {project.battery_manufacturer}
                              {project.battery_model && <span className="text-slate-400 ml-1">— {project.battery_model}</span>}
                              {project.battery_quantity > 0 && <span className="text-slate-400 ml-1">x{project.battery_quantity}</span>}
                            </span>
                          </div>
                        )}
                        {project.ev_charger_manufacturer && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">EV Charger</span>
                            <span className="font-medium text-right">
                              {project.ev_charger_manufacturer}
                              {project.ev_charger_model && <span className="text-slate-400 ml-1">— {project.ev_charger_model}</span>}
                              {project.ev_charger_quantity > 0 && <span className="text-slate-400 ml-1">x{project.ev_charger_quantity}</span>}
                            </span>
                          </div>
                        )}
                        {Array.isArray(project.additional_equipment) && project.additional_equipment
                          .filter((l) => l && (l.manufacturer || l.model))
                          .map((l, idx) => {
                            const catLabel = { panel: 'Solar Panels', inverter: 'Inverter', battery: 'Battery', ev_charger: 'EV Charger' }[l.category] || 'Equipment';
                            return (
                              <div key={`addl-${idx}`} className="flex justify-between text-sm" data-testid={`portal-additional-equipment-${idx}`}>
                                <span className="text-slate-500">{catLabel}</span>
                                <span className="font-medium text-right">
                                  {l.manufacturer}
                                  {l.model && <span className="text-slate-400 ml-1">— {l.model}</span>}
                                  {l.quantity > 0 && <span className="text-slate-400 ml-1">x{l.quantity}</span>}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                      {project.additional_extras && project.additional_extras.trim() && (
                        <div className="pt-3 border-t">
                          <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Additional Extras Included</p>
                          <div className="space-y-1" data-testid="portal-additional-extras">
                            {project.additional_extras
                              .split(/\r?\n/)
                              .map(line => line.replace(/^[•\-*]\s*/, '').trim())
                              .filter(Boolean)
                              .map((extra, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                                  <span className="text-emerald-500 mt-0.5">✓</span>
                                  <span>{extra}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}
                      <div className="pt-3 border-t">
                        <div className="flex flex-wrap gap-2">
                          {project.system_type_solar && <Badge className="bg-amber-100 text-amber-700">Solar</Badge>}
                          {project.system_type_solar_battery && <Badge className="bg-emerald-100 text-emerald-700">Battery</Badge>}
                          {project.system_type_electrical && <Badge className="bg-blue-100 text-blue-700">Electrical</Badge>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Installation Progress */}
                  {checklistProgress && checklistProgress.total > 0 && (
                    <Card className="lg:col-span-2 border-2 border-amber-100" data-testid="portal-checklist-progress">
                      <CardHeader>
                        <CardTitle className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <ClipboardCheck className="w-5 h-5 text-amber-600" />
                            Installation Progress
                          </div>
                          <Badge className={`${
                            checklistProgress.percentage === 100 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : 'bg-amber-100 text-amber-700'
                          }`}>
                            {checklistProgress.percentage}% Complete
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <div className="flex justify-between text-sm text-slate-500 mb-2">
                            <span>{checklistProgress.completed} of {checklistProgress.total} tasks completed</span>
                            <span className="font-semibold text-slate-700">{checklistProgress.percentage}%</span>
                          </div>
                          <Progress value={checklistProgress.percentage} className="h-3" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                          {checklistProgress.items.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2 py-1.5">
                              {item.completed ? (
                                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-slate-200 flex-shrink-0" />
                              )}
                              <span className={`text-sm ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700'}`}>
                                {item.title}
                              </span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </TabsContent>


              {/* Timeline Tab */}
              <TabsContent value="timeline">
                <Card data-testid="portal-timeline">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Milestone className="w-5 h-5 text-blue-600" />
                      Project Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {!timeline || timeline.milestones?.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">Timeline not available yet</p>
                    ) : (
                      <div className="relative">
                        {timeline.install_date && (
                          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200 flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-blue-600" />
                            <div>
                              <p className="text-sm text-blue-600 font-medium">Estimated Installation Date</p>
                              <p className="font-bold text-blue-900">{formatDate(timeline.install_date)}</p>
                            </div>
                          </div>
                        )}
                        <div className="space-y-0">
                          {timeline.milestones.map((milestone, index) => {
                            const isCompleted = milestone.status === 'completed';
                            const isCurrent = milestone.status === 'current';
                            const isUpcoming = milestone.status === 'upcoming';
                            const isLast = index === timeline.milestones.length - 1;

                            return (
                              <div key={milestone.stage} className="flex gap-4" data-testid={`milestone-${milestone.stage}`}>
                                {/* Vertical line + circle */}
                                <div className="flex flex-col items-center">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 flex-shrink-0 transition-all ${
                                    isCompleted
                                      ? 'bg-emerald-500 border-emerald-500 text-white'
                                      : isCurrent
                                        ? 'bg-blue-500 border-blue-500 text-white ring-4 ring-blue-100'
                                        : 'bg-white border-slate-300 text-slate-400'
                                  }`}>
                                    {isCompleted ? (
                                      <CheckCircle className="w-4 h-4" />
                                    ) : isCurrent ? (
                                      <Zap className="w-4 h-4" />
                                    ) : (
                                      <span className="text-xs font-medium">{index + 1}</span>
                                    )}
                                  </div>
                                  {!isLast && (
                                    <div className={`w-0.5 flex-1 min-h-[40px] ${
                                      isCompleted ? 'bg-emerald-300' : 'bg-slate-200'
                                    }`} />
                                  )}
                                </div>

                                {/* Content */}
                                <div className={`pb-8 flex-1 ${isUpcoming ? 'opacity-50' : ''}`}>
                                  <div className="flex items-center gap-2">
                                    <h3 className={`font-semibold ${
                                      isCurrent ? 'text-blue-700' : isCompleted ? 'text-slate-900' : 'text-slate-500'
                                    }`}>
                                      {milestone.title}
                                    </h3>
                                    {isCurrent && (
                                      <Badge className="bg-blue-100 text-blue-700 text-[10px] px-2">Current</Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-500 mt-0.5">{milestone.description}</p>
                                  {milestone.date && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      <Clock className="w-3 h-3 inline mr-1" />
                                      {formatDate(milestone.date)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Billing Tab */}
              <TabsContent value="billing">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Deposit Card */}
                  <Card className={`border-2 ${project.deposit_paid_date ? 'border-emerald-200 bg-emerald-50/30' : project.deposit_due_date && new Date(project.deposit_due_date) < new Date() ? 'border-red-200 bg-red-50/30' : 'border-blue-200 bg-blue-50/30'}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5" />
                          Deposit
                        </div>
                        {project.deposit_paid_date ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Paid
                          </Badge>
                        ) : project.deposit_due_date && new Date(project.deposit_due_date) < new Date() ? (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Overdue
                          </Badge>
                        ) : project.deposit_amount > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        ) : null}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-500 mb-1">Amount Due</p>
                        <p className="text-3xl font-bold text-slate-900">
                          {formatCurrency(project.deposit_amount || 0)}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        <div>
                          <p className="text-sm text-slate-500">Due Date</p>
                          <p className="font-medium">
                            {project.deposit_due_date ? formatDate(project.deposit_due_date) : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Paid Date</p>
                          <p className="font-medium">
                            {project.deposit_paid_date ? formatDate(project.deposit_paid_date) : '-'}
                          </p>
                        </div>
                      </div>
                      {(() => {
                        const dep = invoices.find(i => i.invoice_type === 'deposit');
                        if (!dep) return null;
                        const isPaid = dep.status === 'PAID' || project.deposit_paid_date;
                        return (
                          <Button
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => handleDownloadInvoicePdf(dep.id, isPaid ? `Deposit-Receipt-${dep.xero_invoice_number || ''}` : `Deposit-Invoice-${dep.xero_invoice_number || ''}`)}
                            data-testid="download-deposit-receipt"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {isPaid ? 'Download Receipt' : 'Download Invoice'}
                          </Button>
                        );
                      })()}
                    </CardContent>
                  </Card>

                  {/* Final Bill Card */}
                  <Card className={`border-2 ${project.final_bill_paid_date ? 'border-emerald-200 bg-emerald-50/30' : project.final_bill_due_date && new Date(project.final_bill_due_date) < new Date() ? 'border-red-200 bg-red-50/30' : 'border-amber-200 bg-amber-50/30'}`}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Receipt className="w-5 h-5" />
                          Final Bill
                        </div>
                        {project.final_bill_paid_date ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Paid
                          </Badge>
                        ) : project.final_bill_due_date && new Date(project.final_bill_due_date) < new Date() ? (
                          <Badge className="bg-red-100 text-red-700">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Overdue
                          </Badge>
                        ) : project.final_bill_amount > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700">
                            <Clock className="w-3 h-3 mr-1" />
                            Pending
                          </Badge>
                        ) : null}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="text-center py-4">
                        <p className="text-sm text-slate-500 mb-1">Amount Due</p>
                        <p className="text-3xl font-bold text-slate-900">
                          {formatCurrency(project.final_bill_amount || 0)}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        <div>
                          <p className="text-sm text-slate-500">Due Date</p>
                          <p className="font-medium">
                            {project.final_bill_due_date ? formatDate(project.final_bill_due_date) : '-'}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-500">Paid Date</p>
                          <p className="font-medium">
                            {project.final_bill_paid_date ? formatDate(project.final_bill_paid_date) : '-'}
                          </p>
                        </div>
                      </div>
                      {(() => {
                        const fin = invoices.find(i => i.invoice_type === 'final_payment' || i.invoice_type === 'final');
                        if (!fin) return null;
                        const isPaid = fin.status === 'PAID' || project.final_bill_paid_date;
                        return (
                          <Button
                            variant="outline"
                            className="w-full mt-2"
                            onClick={() => handleDownloadInvoicePdf(fin.id, isPaid ? `Final-Receipt-${fin.xero_invoice_number || ''}` : `Final-Invoice-${fin.xero_invoice_number || ''}`)}
                            data-testid="download-final-receipt"
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {isPaid ? 'Download Receipt' : 'Download Invoice'}
                          </Button>
                        );
                      })()}
                    </CardContent>
                  </Card>
                </div>

                {/* Variations to Sign — e-signature flow for unsigned VOC PDFs */}
                {variations.filter(v => !v.signed).length > 0 && (
                  <Card className="mt-6 border-2 border-amber-300 bg-amber-50/40" data-testid="portal-variations-to-sign">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <FileSignature className="w-5 h-5 text-amber-600" />
                        Action Needed — Sign Variation of Contract
                      </CardTitle>
                      <p className="text-xs text-slate-600 mt-1">
                        Your installer has issued a change to the original contract. Please review and sign to acknowledge.
                      </p>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {variations.filter(v => !v.signed).map((v) => (
                        <div key={v.document_id} className="flex items-center justify-between gap-3 p-3 rounded border border-amber-200 bg-white" data-testid={`portal-variation-sign-${v.document_id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{v.filename}</p>
                            {v.variation_summary?.adjusted_total !== undefined && (
                              <p className="text-[11px] text-slate-500">New total: {formatCurrency(v.variation_summary.adjusted_total)}</p>
                            )}
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button asChild variant="outline" size="sm">
                              <a href={`/api/portal/variations/${v.document_id}/file`} target="_blank" rel="noreferrer">View</a>
                            </Button>
                            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setSignVariation(v)} data-testid={`portal-sign-btn-${v.document_id}`}>
                              <FileSignature className="w-3.5 h-3.5 mr-1" /> Sign
                            </Button>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Unified Contracts & Documents tracker — shows every Contract
                    Pack, Contract Variation and Letter of Authorisation linked
                    to the customer so they can see at a glance what's still
                    awaiting their signature. */}
                {contracts.length > 0 && (() => {
                  const pending = contracts.filter(c => c.needs_customer_signature);
                  const others = contracts.filter(c => !c.needs_customer_signature);
                  const KIND_LABEL = { contract_pack: 'Contract', variation: 'Variation', loa: 'Letter of Authorisation' };
                  const STATUS_PILL = {
                    draft: 'bg-slate-100 text-slate-700 border-slate-200',
                    sent: 'bg-amber-100 text-amber-700 border-amber-200',
                    customer_signed: 'bg-blue-100 text-blue-700 border-blue-200',
                    fully_signed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                    generated: 'bg-slate-100 text-slate-700 border-slate-200',
                    signed: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                  };
                  const STATUS_LABEL = {
                    draft: 'In preparation',
                    sent: 'Awaiting your signature',
                    customer_signed: 'Signed by you — counter-signing',
                    fully_signed: 'Fully signed',
                    generated: 'Prepared',
                    signed: 'Signed',
                  };
                  const SignLink = ({ c }) => {
                    if (!c.sign_token) return null;
                    const path = c.kind === 'contract_pack'
                      ? `/sign-contract/${c.sign_token}`
                      : `/sign-variation/${c.sign_token}`;
                    return (
                      <Button asChild size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid={`portal-contract-sign-${c.id}`}>
                        <a href={path} target="_blank" rel="noreferrer">
                          <FileSignature className="w-3.5 h-3.5 mr-1" /> Sign now
                        </a>
                      </Button>
                    );
                  };
                  const ViewBtn = ({ c }) => {
                    if (!c.view_url) return null;
                    return (
                      <Button size="sm" variant="outline" onClick={() => openContractFile(c)} data-testid={`portal-contract-view-${c.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> View
                      </Button>
                    );
                  };
                  return (
                    <Card className="mt-6" data-testid="portal-contracts-card">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <FileSignature className="w-5 h-5 text-violet-600" />
                          Contracts, Variations &amp; Letters of Authorisation
                          {pending.length > 0 && (
                            <Badge className="ml-2 bg-amber-600 text-white border-amber-700">
                              {pending.length} awaiting you
                            </Badge>
                          )}
                        </CardTitle>
                        <p className="text-xs text-slate-600 mt-1">
                          Every signing document linked to your project — clearly flagged when we&apos;re waiting on you.
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {pending.map((c) => (
                          <div key={`${c.kind}-${c.id}`} className="flex items-center justify-between gap-3 p-3 rounded border-2 border-amber-300 bg-amber-50" data-testid={`portal-contract-${c.kind}-${c.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                                <Badge variant="outline" className="text-[10px] uppercase">{KIND_LABEL[c.kind]}</Badge>
                                <Badge className={`${STATUS_PILL[c.status]} border text-[11px]`}>{STATUS_LABEL[c.status]}</Badge>
                              </div>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Sent {c.sent_at ? new Date(c.sent_at).toLocaleDateString('en-GB') : '—'}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0"><ViewBtn c={c} /><SignLink c={c} /></div>
                          </div>
                        ))}
                        {others.map((c) => (
                          <div key={`${c.kind}-${c.id}`} className="flex items-center justify-between gap-3 p-3 rounded border border-slate-200 bg-white" data-testid={`portal-contract-${c.kind}-${c.id}`}>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-slate-800">{c.label}</span>
                                <Badge variant="outline" className="text-[10px] uppercase">{KIND_LABEL[c.kind]}</Badge>
                                <Badge className={`${STATUS_PILL[c.status] || STATUS_PILL.generated} border text-[11px]`}>{STATUS_LABEL[c.status] || c.status}</Badge>
                              </div>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                {c.signed_at
                                  ? `Signed ${new Date(c.signed_at).toLocaleDateString('en-GB')}`
                                  : c.customer_signed_at
                                  ? `You signed ${new Date(c.customer_signed_at).toLocaleDateString('en-GB')} — Helios counter-signing`
                                  : c.created_at
                                  ? `Created ${new Date(c.created_at).toLocaleDateString('en-GB')}`
                                  : ''}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0"><ViewBtn c={c} /></div>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  );
                })()}

                {/* Signed Documents — dedicated section showing when the
                    customer signed their Contract, Letter of Authorisation and
                    any Contract Variation, each with a View button. */}
                {(() => {
                  const signed = contracts.filter(
                    (c) => c.signed_at || c.customer_signed_at || c.status === 'signed' || c.status === 'fully_signed'
                  );
                  if (signed.length === 0) return null;
                  const KIND_LABEL2 = { contract_pack: 'Contract', variation: 'Variation', loa: 'Letter of Authorisation' };
                  return (
                    <Card className="mt-6 border-emerald-200" data-testid="portal-signed-docs-card">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                          Signed Documents
                          <Badge className="ml-2 bg-emerald-100 text-emerald-700 border-emerald-200">{signed.length}</Badge>
                        </CardTitle>
                        <p className="text-xs text-slate-600 mt-1">
                          Your signed paperwork and the date each was signed — view or download any time.
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {signed.map((c) => {
                          const when = c.signed_at || c.customer_signed_at;
                          return (
                            <div key={`signed-${c.kind}-${c.id}`} className="flex items-center justify-between gap-3 p-3 rounded border border-emerald-200 bg-emerald-50/50" data-testid={`portal-signed-${c.kind}-${c.id}`}>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-slate-800">{c.label}</span>
                                  <Badge variant="outline" className="text-[10px] uppercase">{KIND_LABEL2[c.kind]}</Badge>
                                </div>
                                <p className="text-[11px] text-emerald-700 mt-0.5 flex items-center gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  {when ? `Signed ${new Date(when).toLocaleDateString('en-GB')}` : 'Signed'}
                                  {c.status === 'customer_signed' ? ' — Helios counter-signing' : ''}
                                </p>
                              </div>
                              {c.view_url && (
                                <Button size="sm" variant="outline" onClick={() => openContractFile(c)} className="flex-shrink-0" data-testid={`portal-signed-view-${c.id}`}>
                                  <Eye className="w-3.5 h-3.5 mr-1" /> View
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })()}


                {/* Variations Card — separate row of cards, one per variation invoice */}
                {(() => {
                  const variations = invoices.filter(i => i.invoice_type === 'variation');
                  if (variations.length === 0) return null;
                  return (
                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="portal-variations-grid">
                      {variations.map((vinv) => {
                        const isPaid = vinv.status === 'PAID' || vinv.paid_via_stripe;
                        return (
                          <Card
                            key={vinv.id}
                            className={`border-2 ${isPaid ? 'border-emerald-200 bg-emerald-50/30' : 'border-purple-200 bg-purple-50/30'}`}
                            data-testid={`portal-variation-${vinv.id}`}
                          >
                            <CardHeader>
                              <CardTitle className="text-base flex items-center justify-between">
                                <span className="flex items-center gap-2">
                                  <FileSignature className="w-5 h-5 text-purple-600" />
                                  Customer Variation
                                </span>
                                {isPaid ? (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Paid</Badge>
                                ) : (
                                  <Badge className="bg-purple-100 text-purple-700 border-purple-200">Due</Badge>
                                )}
                              </CardTitle>
                              <p className="text-xs text-slate-500 mt-1">{vinv.xero_invoice_number || 'Variation'}</p>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div>
                                <p className="text-xs text-slate-500 mb-1">Amount</p>
                                <p className="text-3xl font-bold text-slate-900">{formatCurrency(vinv.amount || 0)}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-2 text-xs">
                                <div>
                                  <p className="text-slate-500">Issued</p>
                                  <p className="font-medium">{vinv.created_at ? formatDate(vinv.created_at) : '-'}</p>
                                </div>
                                <div>
                                  <p className="text-slate-500">Due</p>
                                  <p className="font-medium">{vinv.due_date ? formatDate(vinv.due_date) : '-'}</p>
                                </div>
                              </div>
                              {!isPaid && (
                                <Button
                                  className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                                  onClick={() => handlePayInvoice(vinv)}
                                  data-testid={`pay-variation-${vinv.id}`}
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Pay Variation
                                </Button>
                              )}
                              {vinv.xero_invoice_id && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="w-full"
                                  onClick={() => handleDownloadInvoicePdf(vinv.id, `Variation-${vinv.xero_invoice_number || vinv.id.slice(0, 6)}`)}
                                  data-testid={`download-variation-${vinv.id}`}
                                >
                                  <Download className="w-4 h-4 mr-2" />
                                  {isPaid ? 'Download Receipt' : 'Download Invoice'}
                                </Button>
                              )}
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* Payment Summary */}
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PoundSterling className="w-5 h-5" />
                      Payment Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const variations = invoices.filter(i => i.invoice_type === 'variation');
                      const variationsTotal = variations.reduce((s, i) => s + (i.amount || 0), 0);
                      const variationsPaid = variations
                        .filter(i => i.status === 'PAID' || i.paid_via_stripe)
                        .reduce((s, i) => s + (i.amount || 0), 0);
                      const variationsOutstanding = variationsTotal - variationsPaid;
                      const totalCost = (project.system_total_cost || 0); // includes adjustments after VoC
                      const totalPaid =
                        (project.deposit_paid_date ? project.deposit_amount : 0) +
                        (project.final_bill_paid_date ? project.final_bill_amount : 0) +
                        variationsPaid;
                      const outstanding =
                        (project.deposit_paid_date ? 0 : project.deposit_amount || 0) +
                        (project.final_bill_paid_date ? 0 : project.final_bill_amount || 0) +
                        variationsOutstanding;
                      return (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="p-4 bg-slate-50 rounded-lg">
                              <p className="text-sm text-slate-500 mb-1">Total Project Cost</p>
                              <p className="text-2xl font-bold text-slate-900">
                                {formatCurrency(totalCost)}
                              </p>
                              {variationsTotal > 0 && (
                                <p className="text-[11px] text-purple-600 mt-1">inc. {formatCurrency(variationsTotal)} variations</p>
                              )}
                            </div>
                            <div className="p-4 bg-emerald-50 rounded-lg">
                              <p className="text-sm text-slate-500 mb-1">Amount Paid</p>
                              <p className="text-2xl font-bold text-emerald-700">{formatCurrency(totalPaid)}</p>
                            </div>
                            <div className="p-4 bg-amber-50 rounded-lg">
                              <p className="text-sm text-slate-500 mb-1">Outstanding Balance</p>
                              <p className="text-2xl font-bold text-amber-700">{formatCurrency(outstanding)}</p>
                            </div>
                          </div>
                          {project.on_finance && project.finance_amount > 0 && (
                            <div className="mt-4 p-4 bg-indigo-50 border border-indigo-100 rounded-lg" data-testid="portal-finance-note">
                              <div className="flex items-center justify-between flex-wrap gap-2">
                                <div className="flex items-center gap-2">
                                  <CreditCard className="w-4 h-4 text-indigo-600" />
                                  <p className="text-sm font-semibold text-indigo-900">Funded by Finance</p>
                                </div>
                                <p className="text-xl font-bold text-indigo-700">{formatCurrency(project.finance_amount)}</p>
                              </div>
                              <p className="text-xs text-indigo-700/80 mt-1">
                                {formatCurrency(project.finance_amount)} of your total project cost is funded by finance and is
                                paid directly to Helios by your finance provider. The deposit and balance above are the amount
                                payable by you.
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <p className="text-sm text-slate-500 mt-4 text-center">
                      For payment enquiries, please contact Helios Electrical & Solar or raise a support ticket.
                    </p>
                  </CardContent>
                </Card>

                {/* Quotes Section */}
                {quotes.length > 0 && (
                  <Card className="mt-6">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5" />
                        Quotes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {quotes.map((q) => (
                        <div key={q.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg" data-testid={`portal-quote-${q.xero_invoice_number}`}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-900">{q.xero_invoice_number || 'Quote'}</span>
                              <Badge variant="outline" className="text-xs">{q.status || 'DRAFT'}</Badge>
                            </div>
                            <div className="text-xs text-slate-500 mt-0.5">
                              {q.created_at ? formatDate(q.created_at) : ''}
                              {q.due_date ? ` · expires ${formatDate(q.due_date)}` : ''}
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-slate-900">{formatCurrency(q.amount || 0)}</span>
                            {q.xero_invoice_id && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadInvoicePdf(q.id, `Quote-${q.xero_invoice_number || ''}`)}
                                data-testid={`download-quote-${q.xero_invoice_number}`}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* Invoices Section */}
                {invoices.length > 0 && (
                  <Card className="mt-6" data-testid="portal-invoices-section">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-blue-600" />
                        Invoices
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {invoices.map((inv) => {
                          const isPaid = inv.status === 'PAID' || inv.paid_via_stripe;
                          const isOverdue = !isPaid && inv.due_date && new Date(inv.due_date) < new Date();
                          return (
                            <div
                              key={inv.id}
                              className={`p-4 rounded-lg border-2 flex items-center justify-between ${
                                isPaid ? 'border-emerald-200 bg-emerald-50/30' :
                                isOverdue ? 'border-red-200 bg-red-50/30' :
                                'border-slate-200 bg-white'
                              }`}
                              data-testid={`invoice-${inv.id}`}
                            >
                              <div className="flex items-center gap-4">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                  isPaid ? 'bg-emerald-100' : isOverdue ? 'bg-red-100' : 'bg-blue-100'
                                }`}>
                                  {isPaid ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                  ) : (
                                    <FileText className={`w-5 h-5 ${isOverdue ? 'text-red-600' : 'text-blue-600'}`} />
                                  )}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium text-slate-900">
                                      {inv.xero_invoice_number || 'Invoice'}
                                    </span>
                                    <Badge className={
                                      isPaid ? 'bg-emerald-100 text-emerald-700 border-emerald-200' :
                                      isOverdue ? 'bg-red-100 text-red-700 border-red-200' :
                                      inv.status === 'AUTHORISED' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                                      'bg-slate-100 text-slate-600 border-slate-200'
                                    }>
                                      {isPaid ? 'Paid' : isOverdue ? 'Overdue' : inv.status || 'Draft'}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-slate-500">
                                    {inv.description}
                                    {inv.due_date && <span className="ml-2">&middot; Due {formatDate(inv.due_date)}</span>}
                                  </p>
                                  {isPaid && (() => {
                                    const via = (inv.payment_method || '').toLowerCase()
                                      || ((inv.paid_via || '').toLowerCase().startsWith('stripe') || inv.paid_via_stripe ? 'card'
                                        : ((inv.paid_via || '').toLowerCase().includes('xero') ? 'bank_transfer' : ''));
                                    const label = via === 'card' ? 'Paid by card (Stripe)'
                                      : via === 'bank_transfer' ? 'Paid by bank transfer' : null;
                                    return label ? (
                                      <p className="text-xs text-emerald-600 mt-0.5" data-testid={`invoice-paid-method-${inv.id}`}>
                                        {label}
                                      </p>
                                    ) : null;
                                  })()}
                                </div>
                              </div>
                              <div className="flex items-center gap-4">
                                <span className="text-xl font-bold text-slate-900">
                                  {formatCurrency(inv.amount || 0)}
                                </span>
                                {!isPaid && inv.amount > 0 && inv.is_single_invoice ? (
                                  <div className="flex flex-col items-end gap-1">
                                    {!inv.deposit_paid ? (
                                      <Button
                                        onClick={() => handlePayInvoice(inv, 'deposit')}
                                        disabled={payingInvoice === `${inv.id}-deposit`}
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                        data-testid={`pay-deposit-${inv.id}`}
                                      >
                                        {payingInvoice === `${inv.id}-deposit` ? (
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                          <CreditCard className="w-4 h-4 mr-2" />
                                        )}
                                        Pay {inv.deposit_percent ? `${inv.deposit_percent}% ` : ''}deposit ({formatCurrency(inv.deposit_amount || 0)})
                                      </Button>
                                    ) : (
                                      <Button
                                        onClick={() => handlePayInvoice(inv, 'balance')}
                                        disabled={payingInvoice === `${inv.id}-balance`}
                                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                        data-testid={`pay-balance-${inv.id}`}
                                      >
                                        {payingInvoice === `${inv.id}-balance` ? (
                                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                          <CreditCard className="w-4 h-4 mr-2" />
                                        )}
                                        Pay balance ({formatCurrency(inv.balance_amount || 0)})
                                      </Button>
                                    )}
                                    {inv.deposit_paid && (
                                      <span className="text-xs text-emerald-600" data-testid={`deposit-paid-${inv.id}`}>
                                        Deposit paid &middot; balance outstanding
                                      </span>
                                    )}
                                  </div>
                                ) : !isPaid && inv.amount > 0 ? (
                                  <Button
                                    onClick={() => handlePayInvoice(inv)}
                                    disabled={payingInvoice === inv.id}
                                    className="bg-blue-600 hover:bg-blue-700 text-white"
                                    data-testid={`pay-invoice-${inv.id}`}
                                  >
                                    {payingInvoice === inv.id ? (
                                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                      <CreditCard className="w-4 h-4 mr-2" />
                                    )}
                                    Pay Now
                                  </Button>
                                ) : null}
                                {isPaid && inv.stripe_paid_at && (
                                  <span className="text-xs text-emerald-600">
                                    Paid {formatDate(inv.stripe_paid_at)}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              {/* Progress History Tab */}
              <TabsContent value="progress">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Progress Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {statusHistory.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No status updates yet</p>
                    ) : (
                      <div className="space-y-4">
                        {statusHistory.map((entry, index) => (
                          <div key={index} className="flex gap-4">
                            <div className="flex flex-col items-center">
                              <div className="w-3 h-3 bg-blue-600 rounded-full" />
                              {index < statusHistory.length - 1 && (
                                <div className="w-0.5 h-full bg-slate-200 mt-1" />
                              )}
                            </div>
                            <div className="flex-1 pb-6">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={STATUS_COLORS[entry.status] || 'bg-slate-100'}>
                                  {entry.status_display || STATUS_DISPLAY_NAMES[entry.status]}
                                </Badge>
                                <span className="text-sm text-slate-500">
                                  {formatDate(entry.changed_at)}
                                </span>
                              </div>
                              {entry.notes && (
                                <p className="text-sm text-slate-600">{entry.notes}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Documents Tab */}
              <TabsContent value="documents">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Project Documents
                    </CardTitle>
                    <div>
                      <input
                        type="file"
                        id="portal-doc-upload"
                        className="hidden"
                        onChange={handleDocumentUpload}
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xls,.xlsx,.csv,.txt"
                      />
                      <Button
                        onClick={() => document.getElementById('portal-doc-upload').click()}
                        disabled={uploading}
                        data-testid="portal-upload-btn"
                      >
                        {uploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload Document
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {documents.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No documents uploaded yet</p>
                    ) : (() => {
                      // Filter by file name across folders, then group by folder.
                      const q = docSearch.trim().toLowerCase();
                      const filtered = q
                        ? documents.filter((d) => (d.filename || '').toLowerCase().includes(q))
                        : documents;
                      const groups = {};
                      for (const doc of filtered) {
                        const key = (doc.folder || '').trim() || 'General';
                        (groups[key] = groups[key] || []).push(doc);
                      }
                      const folderNames = Object.keys(groups).sort((a, b) =>
                        a === 'General' ? 1 : b === 'General' ? -1 : a.localeCompare(b));
                      const renderDoc = (doc) => (
                        <div key={doc.id} className="border rounded-lg p-4 hover:border-blue-300 transition-colors" data-testid={`portal-doc-${doc.id}`}>
                          <div className="flex items-start gap-3">
                            {doc.file_type?.startsWith('image') ? (
                              <Image className="w-8 h-8 text-blue-500" />
                            ) : doc.file_type?.startsWith('video') ? (
                              <Eye className="w-8 h-8 text-purple-500" />
                            ) : (
                              <FileText className="w-8 h-8 text-slate-400" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{doc.filename}</p>
                              <p className="text-sm text-slate-500">{formatDate(doc.uploaded_at)}</p>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" className="w-full mt-3" asChild>
                            <a href={doc.url} target="_blank" rel="noopener noreferrer">
                              <Download className="w-4 h-4 mr-2" />
                              Download
                            </a>
                          </Button>
                        </div>
                      );
                      return (
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                              value={docSearch}
                              onChange={(e) => setDocSearch(e.target.value)}
                              placeholder="Search documents by name (e.g. warranty, MCS, certificate)…"
                              className="pl-9"
                              data-testid="portal-doc-search"
                            />
                          </div>
                          {folderNames.length === 0 ? (
                            <p className="text-center text-slate-500 py-8" data-testid="portal-doc-no-match">
                              No documents match “{docSearch}”.
                            </p>
                          ) : (
                            <Accordion type="multiple" key={`acc-${q}`} defaultValue={folderNames} className="space-y-2">
                              {folderNames.map((name) => (
                                <AccordionItem key={name} value={name} className="border rounded-lg px-3" data-testid={`portal-folder-${name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                                  <AccordionTrigger className="hover:no-underline py-3">
                                    <span className="flex items-center gap-2 text-sm font-semibold">
                                      <Folder className="w-4 h-4 text-amber-500" />
                                      {name}
                                      <Badge variant="secondary" className="ml-1">{groups[name].length}</Badge>
                                    </span>
                                  </AccordionTrigger>
                                  <AccordionContent>
                                    <div className="flex justify-end pb-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="h-7 text-xs"
                                        onClick={() => handleDownloadFolder(name)}
                                        disabled={syncingFolderZip === name}
                                        data-testid={`portal-folder-download-all-${name.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}
                                      >
                                        {syncingFolderZip === name
                                          ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                          : <Download className="w-3 h-3 mr-1" />}
                                        Download all ({groups[name].length})
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-2">
                                      {groups[name].map(renderDoc)}
                                    </div>
                                  </AccordionContent>
                                </AccordionItem>
                              ))}
                            </Accordion>
                          )}
                        </div>
                      );
                    })()}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Emails Tab */}
              <TabsContent value="emails">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Email Communications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {emails.length === 0 ? (
                      <p className="text-center text-slate-500 py-8">No emails sent yet</p>
                    ) : (
                      <div className="space-y-3">
                        {emails.map((email, index) => (
                          <div key={index} className="border rounded-lg p-4">
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <p className="font-medium">{email.subject}</p>
                                <p className="text-sm text-slate-500">{formatDate(email.sent_at)}</p>
                              </div>
                              <Badge className="bg-emerald-100 text-emerald-700">Sent</Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Support Tab */}
              <TabsContent value="support">
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Support Tickets
                    </CardTitle>
                    <Button onClick={() => setTicketDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      New Ticket
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {tickets.length === 0 ? (
                      <div className="text-center py-8">
                        <MessageSquare className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                        <p className="text-slate-500 mb-4">No support tickets yet</p>
                        <Button onClick={() => setTicketDialog(true)}>Create Your First Ticket</Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {tickets.map((ticket) => (
                          <div 
                            key={ticket.id} 
                            onClick={() => setViewTicket(ticket)}
                            className="block border rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <p className="font-medium">{ticket.subject}</p>
                                <p className="text-sm text-slate-500 line-clamp-1">{ticket.description}</p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge className={
                                    ticket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                                    ticket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                                    ticket.status === 'awaiting_customer' ? 'bg-purple-100 text-purple-700' :
                                    (ticket.status === 'resolved' || ticket.status === 'closed') ? 'bg-emerald-100 text-emerald-700' :
                                    'bg-slate-100 text-slate-700'
                                  }>
                                    {ticket.status === 'awaiting_customer' ? 'Awaiting your reply' : ticket.status.replace(/_/g, ' ')}
                                  </Badge>
                                  <span className="text-xs text-slate-400">{formatDate(ticket.created_at)}</span>
                                  {ticket.responses?.length > 0 && (
                                    <span className="text-xs text-slate-400">• {ticket.responses.length} response(s)</span>
                                  )}
                                </div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Messages Tab */}
              <TabsContent value="messages">
                <Card data-testid="portal-messages">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-600" />
                      Messages
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col" style={{ height: '500px' }}>
                      {/* Messages list */}
                      <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-3 bg-slate-50 rounded-lg">
                        {messages.length === 0 ? (
                          <div className="flex flex-col items-center justify-center h-full text-slate-400">
                            <MessageSquare className="w-12 h-12 mb-3 opacity-40" />
                            <p className="font-medium">No messages yet</p>
                            <p className="text-sm mt-1">Send a message to the Helios team below</p>
                          </div>
                        ) : (
                          messages.map((msg) => (
                            <div
                              key={msg.id}
                              className={`flex ${msg.is_customer ? 'justify-end' : 'justify-start'}`}
                              data-testid={`message-${msg.id}`}
                            >
                              <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                                msg.is_customer
                                  ? 'bg-blue-600 text-white rounded-br-sm'
                                  : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm'
                              }`}>
                                {!msg.is_customer && (
                                  <p className="text-xs font-semibold text-blue-600 mb-1">{msg.sender_name}</p>
                                )}
                                <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                                <p className={`text-[10px] mt-1 ${msg.is_customer ? 'text-blue-200' : 'text-slate-400'}`}>
                                  {formatDate(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          ))
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Message input */}
                      <div className="flex gap-2">
                        <Input
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          className="flex-1"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          data-testid="message-input"
                        />
                        <Button
                          onClick={handleSendMessage}
                          disabled={sendingMessage || !newMessage.trim()}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="send-message-btn"
                        >
                          {sendingMessage ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Send className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Gallery Tab */}
              <TabsContent value="gallery">
                <Card data-testid="portal-gallery">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Camera className="w-5 h-5 text-blue-600" />
                      Installation Gallery
                    </CardTitle>
                    <div>
                      <input
                        type="file"
                        id="portal-photo-upload"
                        className="hidden"
                        onChange={handleDocumentUpload}
                        accept="image/*"
                      />
                      <Button
                        variant="outline"
                        onClick={() => document.getElementById('portal-photo-upload').click()}
                        disabled={uploading}
                        data-testid="portal-photo-upload-btn"
                      >
                        {uploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Upload Photo
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {gallery.length === 0 ? (
                      <div className="text-center py-12 text-slate-400">
                        <Camera className="w-16 h-16 mx-auto mb-4 opacity-30" />
                        <p className="text-lg font-medium">No photos yet</p>
                        <p className="text-sm mt-1">Installation photos will appear here as your project progresses</p>
                        <Button
                          variant="outline"
                          className="mt-4"
                          onClick={() => document.getElementById('portal-photo-upload').click()}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Your First Photo
                        </Button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {gallery.map((photo) => (
                          <div
                            key={photo.id}
                            className="relative group cursor-pointer rounded-lg overflow-hidden border border-slate-200 aspect-square bg-slate-100"
                            onClick={() => setLightboxPhoto(photo)}
                            data-testid={`gallery-photo-${photo.id}`}
                          >
                            <img
                              src={photo.url}
                              alt={photo.filename}
                              className="w-full h-full object-cover transition-transform group-hover:scale-105"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                              <div className="p-2 w-full opacity-0 group-hover:opacity-100 transition-opacity">
                                <p className="text-white text-xs truncate">{photo.filename}</p>
                                <p className="text-white/70 text-[10px]">{formatDate(photo.uploaded_at)}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Energy Savings Calculator Tab */}
              <TabsContent value="calculator">
                <Card data-testid="portal-energy-calculator">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      Energy Savings Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Auto-detect button */}
                      <div className="flex gap-3">
                        <Button
                          className="bg-amber-500 hover:bg-amber-600 text-white"
                          onClick={handleAutoCalculate}
                          disabled={calcLoading}
                          data-testid="auto-calculate-btn"
                        >
                          {calcLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                          Auto-Calculate from My System
                        </Button>
                        <Button variant="outline" onClick={handleCalculate} disabled={calcLoading} data-testid="manual-calculate-btn">
                          Calculate
                        </Button>
                      </div>

                      {/* Input sliders */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">System Size (kW)</label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={calcInputs.system_size_kw}
                            onChange={(e) => setCalcInputs(prev => ({ ...prev, system_size_kw: parseFloat(e.target.value) || 0 }))}
                            data-testid="calc-system-size"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">Annual Usage (kWh)</label>
                          <Input
                            type="number"
                            step="100"
                            min="0"
                            value={calcInputs.annual_consumption_kwh}
                            onChange={(e) => setCalcInputs(prev => ({ ...prev, annual_consumption_kwh: parseFloat(e.target.value) || 0 }))}
                            data-testid="calc-consumption"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">Electricity Rate (£/kWh)</label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={calcInputs.tariff_rate}
                            onChange={(e) => setCalcInputs(prev => ({ ...prev, tariff_rate: parseFloat(e.target.value) || 0 }))}
                            data-testid="calc-tariff"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium text-slate-700 dark:text-slate-300 block mb-1">Battery Capacity (kWh)</label>
                          <Input
                            type="number"
                            step="0.5"
                            min="0"
                            value={calcInputs.battery_capacity_kwh}
                            onChange={(e) => setCalcInputs(prev => ({ ...prev, battery_capacity_kwh: parseFloat(e.target.value) || 0 }))}
                            data-testid="calc-battery"
                          />
                        </div>
                      </div>

                      {/* Results */}
                      {calcResult && (
                        <div className="space-y-4" data-testid="calc-results">
                          {/* Key stats */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">£{calcResult.annual_total_savings}</p>
                              <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-1">Annual Savings</p>
                            </div>
                            <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">£{calcResult.monthly_savings}</p>
                              <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">Monthly Savings</p>
                            </div>
                            <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">£{calcResult.total_25yr_savings?.toLocaleString()}</p>
                              <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">25-Year Savings</p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 text-center">
                              <p className="text-2xl font-bold text-green-700 dark:text-green-400">{calcResult.carbon_offset_kg} kg</p>
                              <p className="text-xs text-green-600 dark:text-green-500 mt-1">CO2 Offset/Year</p>
                            </div>
                          </div>

                          {/* Breakdown */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Card className="border-slate-200 dark:border-slate-700">
                              <CardContent className="pt-4 space-y-2">
                                <h4 className="font-semibold text-sm">Generation & Usage</h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Annual Generation</span>
                                    <span className="font-medium">{calcResult.annual_generation_kwh?.toLocaleString()} kWh</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Self-Consumption</span>
                                    <span className="font-medium">{calcResult.self_consumption_pct}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Solar Used at Home</span>
                                    <span className="font-medium">{calcResult.solar_self_consumed_kwh?.toLocaleString()} kWh</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Exported to Grid</span>
                                    <span className="font-medium">{calcResult.solar_exported_kwh?.toLocaleString()} kWh</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                            <Card className="border-slate-200 dark:border-slate-700">
                              <CardContent className="pt-4 space-y-2">
                                <h4 className="font-semibold text-sm">Financial Breakdown</h4>
                                <div className="space-y-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Import Savings</span>
                                    <span className="font-medium text-emerald-600">£{calcResult.annual_import_savings}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-slate-500">Export Income (SEG)</span>
                                    <span className="font-medium text-emerald-600">£{calcResult.annual_export_income}</span>
                                  </div>
                                  {calcResult.annual_battery_savings > 0 && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-500">Battery Savings</span>
                                      <span className="font-medium text-emerald-600">£{calcResult.annual_battery_savings}</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between border-t pt-1 mt-1">
                                    <span className="font-semibold">Total Annual</span>
                                    <span className="font-bold text-emerald-600">£{calcResult.annual_total_savings}</span>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          </div>

                          {/* 25-year projection preview */}
                          <div className="p-4 bg-gradient-to-r from-amber-50 to-emerald-50 dark:from-amber-950/20 dark:to-emerald-950/20 rounded-lg">
                            <h4 className="font-semibold text-sm mb-2">25-Year Savings Projection</h4>
                            <div className="grid grid-cols-5 gap-2 text-center text-xs">
                              {[4, 9, 14, 19, 24].map(i => (
                                <div key={i}>
                                  <p className="text-slate-500">Year {i + 1}</p>
                                  <p className="font-bold text-emerald-700 dark:text-emerald-400">
                                    £{calcResult.projection_25yr?.[i]?.cumulative_savings?.toLocaleString() || '0'}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Your Data Tab (GDPR) */}
              <TabsContent value="your-data">
                <Card data-testid="portal-your-data">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      Your Data & Privacy
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="border border-slate-200 dark:border-slate-700 rounded-lg p-5 space-y-3">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Download Your Data</h3>
                      <p className="text-sm text-slate-500">
                        Request a copy of all personal data we hold about you (GDPR Article 15). This includes your customer record, messages, and documents.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await api.get('/gdpr/portal/export', { responseType: 'blob' });
                            const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a'); a.href = url; a.download = 'my-data-export.json'; a.click();
                            URL.revokeObjectURL(url);
                            toast.success('Data export downloaded');
                          } catch { toast.error('Failed to export data'); }
                        }}
                        data-testid="export-data-btn"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download My Data (JSON)
                      </Button>
                    </div>

                    <div className="border border-red-200 dark:border-red-900/50 rounded-lg p-5 space-y-3 bg-red-50/50 dark:bg-red-950/20">
                      <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">Request Data Deletion</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-400">
                        Request deletion of your personal data (GDPR Article 17). Note: Some records may be retained for legal obligations (e.g., tax records for 6 years).
                        We will process your request within 30 days.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-400"
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to request deletion of your data? This action cannot be undone once processed.')) return;
                          try {
                            await api.post('/gdpr/portal/deletion-request', { reason: 'Customer self-service request' });
                            toast.success('Deletion request submitted. We will process it within 30 days.');
                          } catch (err) {
                            const msg = err.response?.data?.detail || 'Failed to submit request';
                            toast.error(msg);
                          }
                        }}
                        data-testid="deletion-request-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Request Deletion
                      </Button>
                    </div>

                    <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 dark:text-blue-400 underline hover:no-underline">
                        View our Privacy Policy
                      </a>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>


            </Tabs>
          </>
        )}
      </main>

      {/* New Ticket Dialog */}
      <Dialog open={ticketDialog} onOpenChange={setTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select 
                value={newTicket.category} 
                onValueChange={(v) => setNewTicket({...newTicket, category: v})}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TICKET_CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subject</Label>
              <Input
                value={newTicket.subject}
                onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                placeholder="Brief description of your issue"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newTicket.description}
                onChange={(e) => setNewTicket({...newTicket, description: e.target.value})}
                placeholder="Please provide details about your enquiry..."
                rows={5}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateTicket} disabled={submittingTicket}>
              {submittingTicket ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={!!viewTicket} onOpenChange={(open) => !open && setViewTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          {viewTicket && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-start justify-between gap-4 pr-8">
                  <span className="text-lg">{viewTicket.subject}</span>
                </DialogTitle>
                <div className="flex items-center gap-2 pt-2">
                  <Badge className={
                    viewTicket.status === 'open' ? 'bg-blue-100 text-blue-700' :
                    viewTicket.status === 'in_progress' ? 'bg-amber-100 text-amber-700' :
                    viewTicket.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-slate-100 text-slate-700'
                  }>
                    {viewTicket.status.replace('_', ' ')}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    Created {formatDate(viewTicket.created_at)}
                  </span>
                </div>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto space-y-4 py-4">
                {/* Original Message */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm">Your Message</span>
                    <Badge variant="outline" className="text-xs">{viewTicket.category}</Badge>
                  </div>
                  <p className="text-slate-700 whitespace-pre-wrap">{viewTicket.description}</p>
                </div>

                {/* Responses */}
                {viewTicket.responses && viewTicket.responses.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-slate-900">Conversation</h4>
                    {viewTicket.responses.map((response, index) => (
                      <div 
                        key={index}
                        className={`p-4 rounded-lg ${
                          response.is_customer 
                            ? 'bg-blue-50 ml-4' 
                            : 'bg-emerald-50 mr-4 border-l-4 border-emerald-500'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">
                            {response.is_customer ? 'You' : `${response.user_name} (Helios Team)`}
                          </span>
                          <span className="text-xs text-slate-500">
                            {formatDate(response.created_at)}
                          </span>
                        </div>
                        <p className="text-slate-700 whitespace-pre-wrap">{response.message}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Section */}
                {viewTicket.status !== 'closed' && (
                  <div className="space-y-3 pt-4 border-t">
                    <Label>Reply</Label>
                    <Textarea
                      value={ticketReply}
                      onChange={(e) => setTicketReply(e.target.value)}
                      placeholder="Type your message..."
                      rows={3}
                    />
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => setViewTicket(null)}>
                  Close
                </Button>
                {viewTicket.status !== 'closed' && (
                  <Button onClick={handleSendReply} disabled={sendingReply || !ticketReply.trim()}>
                    {sendingReply ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Send Reply
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox */}
      <Dialog open={!!lightboxPhoto} onOpenChange={(open) => !open && setLightboxPhoto(null)}>
        <DialogContent className="max-w-3xl p-2">
          {lightboxPhoto && (
            <div>
              <img
                src={lightboxPhoto.url}
                alt={lightboxPhoto.filename}
                className="w-full max-h-[75vh] object-contain rounded-lg"
              />
              <div className="px-2 py-3 flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">{lightboxPhoto.filename}</p>
                  <p className="text-xs text-slate-500">{formatDate(lightboxPhoto.uploaded_at)}</p>
                </div>
                {lightboxPhoto.caption && (
                  <p className="text-sm text-slate-600">{lightboxPhoto.caption}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Company Footer */}
      <footer className="mt-12 py-8 border-t border-slate-200 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img 
                src={LOGO_DARK_URL} 
                alt="Helios Electrical & Solar" 
                className="h-10 w-auto"
              />
            </div>
            <div className="text-center md:text-right space-y-1">
              <p className="text-sm text-slate-600">
                <MapPin className="w-4 h-4 inline mr-1" />
                51 Burners Lane South, Kiln Farm, Milton Keynes, MK11 3HA
              </p>
              <p className="text-sm text-slate-600">
                <Phone className="w-4 h-4 inline mr-1" />
                <a href="tel:01908348250" className="hover:text-blue-600">01908 348250</a>
                <span className="mx-3">|</span>
                <Mail className="w-4 h-4 inline mr-1" />
                <a href="mailto:hello@helioses.co.uk" className="hover:text-blue-600">hello@helioses.co.uk</a>
              </p>
            </div>
          </div>
          <p className="text-xs text-slate-400 text-center mt-6">
            © {new Date().getFullYear()} Helios Electrical & Solar. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Decline Quote Dialog */}
      <Dialog open={declineDialog.open} onOpenChange={(o) => !o && !decliningQuote && setDeclineDialog({ open: false, quote: null })}>
        <DialogContent className="max-w-md" data-testid="decline-quote-dialog">
          <DialogHeader>
            <DialogTitle>Decline Quote {declineDialog.quote?.xero_invoice_number}</DialogTitle>
            <DialogDescription>
              Letting us know why helps us improve our service. Your feedback is sent to the Helios team.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="mb-2 block">Main reason</Label>
              <Select value={declineReason} onValueChange={setDeclineReason}>
                <SelectTrigger data-testid="decline-reason-select">
                  <SelectValue placeholder="Pick a reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Price too high">Price too high</SelectItem>
                  <SelectItem value="Going with another provider">Going with another provider</SelectItem>
                  <SelectItem value="Postponing the project">Postponing the project</SelectItem>
                  <SelectItem value="No longer needed">No longer needed</SelectItem>
                  <SelectItem value="Specification doesn't match what I wanted">Specification doesn't match what I wanted</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Additional notes (optional)</Label>
              <Textarea
                value={declineNotes}
                onChange={(e) => setDeclineNotes(e.target.value)}
                placeholder="Anything else you'd like us to know..."
                rows={4}
                data-testid="decline-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialog({ open: false, quote: null })} disabled={decliningQuote}>Cancel</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={handleDeclineQuoteSubmit}
              disabled={decliningQuote || !declineReason}
              data-testid="confirm-decline-quote"
            >
              {decliningQuote ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Submit decline
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SignVariationDialog
        open={!!signVariation}
        onOpenChange={(v) => !v && setSignVariation(null)}
        variation={signVariation}
        defaultName={`${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.name}
        submitFn={async (signature_data_url, signed_name) => {
          const r = await api.post(`/portal/variations/${signVariation.document_id}/sign`, { signature_data_url, signed_name });
          return r.data;
        }}
        onSigned={() => { setSignVariation(null); fetchData(); }}
      />
      <AiImageNameDialog
        prompt={aiPrompt}
        onConfirm={(name) => resolvePortalAiPrompt(name)}
        onSkip={() => resolvePortalAiPrompt(null)}
      />
    </div>
  );
};
